<template>
  <div class="channel-reference-selector">
    <label v-if="label" class="form-label">{{ label }}</label>

    <div class="d-flex flex-wrap align-items-center gap-2">
      <span
        v-for="entity in resolvedEntities"
        :key="entity.ref"
        class="reference-chip badge bg-light text-dark d-inline-flex align-items-center gap-1 py-1 px-2">
        <component :is="iconForType(entity.type)" :size="14" />
        <span
          v-if="entity.status === 'loading'"
          class="spinner-border spinner-border-sm"
          style="width: 12px; height: 12px" />
        <span
          v-else
          class="text-truncate"
          :class="{ 'text-danger': entity.status === 'error' }"
          style="max-width: 180px"
          >{{ entity.name }}</span
        >
        <IconX
          v-if="!disabled"
          :size="14"
          class="ms-1 cursor-pointer text-muted reference-chip-remove"
          @click="removeReference(entity.ref)" />
      </span>

      <div v-if="!disabled" class="reference-popover" ref="popoverRef">
        <button
          type="button"
          class="btn btn-icon rounded-circle d-flex align-items-center justify-content-center"
          style="width: 32px; height: 32px"
          @click.prevent="togglePopover">
          <IconPlus :size="16" />
        </button>
        <div v-if="isPopoverOpen" class="reference-popover-menu card shadow-sm">
          <div v-if="step === 'pick-type'" class="py-1" style="width: 250px">
            <a
              href="#"
              class="reference-popover-item d-flex align-items-center gap-2 py-2 px-3"
              @click.prevent="selectType('customer.account')">
              <IconUser :size="16" /> {{ $t('channel_references.customer_account') }}
            </a>
            <a
              href="#"
              class="reference-popover-item d-flex align-items-center gap-2 py-2 px-3"
              @click.prevent="selectType('company')">
              <IconBuilding :size="16" /> {{ $t('channel_references.company') }}
            </a>
            <a
              href="#"
              class="reference-popover-item d-flex align-items-center gap-2 py-2 px-3"
              @click.prevent="selectType('product')">
              <IconPackage :size="16" /> {{ $t('channel_references.product') }}
            </a>
          </div>

          <div v-else class="p-3" style="width: 320px">
            <a
              href="#"
              class="small text-muted d-inline-flex align-items-center gap-1 mb-2"
              @click.prevent="step = 'pick-type'">
              <IconArrowLeft :size="14" /> {{ $t('common.back') }}
            </a>

            <CompanySelector
              v-if="selectedEntityType === 'company'"
              v-model:companyId="tempCompanyId"
              :label="''"
              :required="false"
              :clearable="true"
              @update:companyId="handleCompanySelected" />

            <LeadCombobox
              v-if="selectedEntityType === 'customer.account'"
              :items="accountOptions"
              :modelValue="null"
              :loading="isSearching"
              :placeholder="$t('channel_references.search_accounts')"
              getOptionLabel="name"
              getOptionValue="id"
              displayInitials
              @update:modelValue="handleAccountSelected"
              @search="searchAccounts" />

            <LeadCombobox
              v-if="selectedEntityType === 'product'"
              :items="productOptions"
              :modelValue="null"
              :loading="isSearching"
              :placeholder="$t('channel_references.search_products')"
              getOptionLabel="name"
              getOptionValue="id"
              @update:modelValue="handleProductSelected" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue';
import { debounce } from '@prospective/lithium';
import { IconPlus, IconX, IconUser, IconBuilding, IconPackage, IconArrowLeft } from '@tabler/icons-vue';
import CompanySelector from '@/components/company/CompanySelector.vue';
import LeadCombobox from '@/components/form/LeadCombobox.vue';
import { fetchCustomerAccounts } from '@/api/customer';
import { fetchProducts } from '@/api/product';
import {
  useEntityReferenceResolver,
  buildRef,
  formatAccountName,
  type EntityType,
} from '@/composables/useEntityReferenceResolver';

interface Props {
  label?: string;
  disabled?: boolean;
}

withDefaults(defineProps<Props>(), {
  label: 'References',
  disabled: false,
});

const modelValue = defineModel<string[]>({ default: () => [] });

/* ----------------------------------- STATE ---------------------------------- */

const step = ref<'pick-type' | 'pick-entity'>('pick-type');
const selectedEntityType = ref<EntityType | null>(null);
const tempCompanyId = ref<number | undefined>(undefined);
const accountOptions = ref<any[]>([]);
const productOptions = ref<any[]>([]);
const isSearching = ref(false);
const isPopoverOpen = ref(false);
const popoverRef = ref<HTMLElement | null>(null);

const { resolvedEntities } = useEntityReferenceResolver(modelValue);

/* --------------------------------- METHODS -------------------------------- */

function iconForType(type: EntityType) {
  if (type === 'customer.account') return IconUser;
  if (type === 'company') return IconBuilding;
  return IconPackage;
}

function handleClickOutside(event: MouseEvent) {
  if (popoverRef.value && !popoverRef.value.contains(event.target as Node)) {
    closePopover();
  }
}

function togglePopover() {
  if (isPopoverOpen.value) {
    closePopover();
  } else {
    step.value = 'pick-type';
    selectedEntityType.value = null;
    tempCompanyId.value = undefined;
    isPopoverOpen.value = true;
    document.addEventListener('mousedown', handleClickOutside);
  }
}

function closePopover() {
  isPopoverOpen.value = false;
  document.removeEventListener('mousedown', handleClickOutside);
}

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});

function selectType(type: EntityType) {
  selectedEntityType.value = type;
  step.value = 'pick-entity';

  if (type === 'customer.account') {
    loadAccounts();
  } else if (type === 'product') {
    loadProducts();
  }
}

function addReference(type: EntityType, id: number) {
  const refStr = buildRef(type, id);
  if (modelValue.value.includes(refStr)) return;
  modelValue.value = [...modelValue.value, refStr];
  closePopover();
}

function removeReference(refStr: string) {
  modelValue.value = modelValue.value.filter((r) => r !== refStr);
}

function handleCompanySelected(id: number | undefined) {
  if (id) {
    addReference('company', id);
    tempCompanyId.value = undefined;
  }
}

function handleAccountSelected(option: any) {
  if (option?.id) {
    addReference('customer.account', option.id);
  }
}

function handleProductSelected(option: any) {
  if (option?.id) {
    addReference('product', option.id);
  }
}

async function loadAccounts(query?: string) {
  isSearching.value = true;
  try {
    const res = await fetchCustomerAccounts(query ? { search: query } : undefined);
    accountOptions.value = (res.data as any[]).map((a: any) => ({
      id: a.id,
      name: formatAccountName(a),
    }));
  } finally {
    isSearching.value = false;
  }
}

const searchAccountsDebounced = debounce((query: string) => {
  loadAccounts(query);
}, 300);

function searchAccounts(query: string) {
  isSearching.value = true;
  searchAccountsDebounced(query);
}

async function loadProducts() {
  if (productOptions.value.length > 0) return;
  isSearching.value = true;
  try {
    const res = await fetchProducts();
    productOptions.value = res.data.map((p) => ({ id: p.id, name: p.name }));
  } finally {
    isSearching.value = false;
  }
}

/* --------------------------------- WATCHERS -------------------------------- */

watch(selectedEntityType, () => {
  tempCompanyId.value = undefined;
});
</script>

<style scoped lang="scss">
.reference-chip {
  font-size: 13px;
  font-weight: 400;
  border-radius: 20px;
}

.reference-chip-remove {
  opacity: 0.5;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
}

.reference-popover {
  position: relative;
  display: inline-block;
}

.reference-popover-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  margin-top: 4px;
  border-radius: 6px;
}

.reference-popover-item {
  color: inherit;
  text-decoration: none;
  border-radius: 4px;
  margin: 0 4px;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f1f5f9;
  }
}
</style>
